'use client'

import Link from 'next/link'
import { useState, useEffect } from 'react'
import { SlideOverSheet, Input, Label, Button, Separator } from '@repo/ui'
import { Mail, Lock, Eye, EyeOff, ChevronDown, Shield, ShieldCheck, Wallet as WalletIcon, ArrowRight } from 'lucide-react'
import { updateEmail, updatePassword } from '@/app/actions/account'
import { getWalletBalance } from '@/app/actions/wallet'
import { createClient } from '@repo/db/browser'

function formatCurrency(amount: number, currency: string = 'TRY') {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount)
}

interface AccountSheetProps {
  open: boolean
  onClose: () => void
  email: string
}

export function AccountSheet({ open, onClose, email }: AccountSheetProps) {
  const [openSection, setOpenSection] = useState<'email' | 'password' | '2fa' | null>(null)

  // E-posta formu
  const [newEmail, setNewEmail] = useState(email)
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailMsg, setEmailMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [emailConfirm, setEmailConfirm] = useState(false)

  // Şifre formu
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)

  // 2FA state
  const [mfaStatus, setMfaStatus] = useState<'loading' | 'off' | 'on'>('loading')
  const [mfaStep, setMfaStep] = useState<'idle' | 'verifying' | 'confirming-disable'>('idle')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [factorId, setFactorId] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)
  const [mfaMsg, setMfaMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Cüzdan bakiyesi
  const [balance, setBalance] = useState<number | null>(null)
  const [currency, setCurrency] = useState<string>('TRY')

  useEffect(() => {
    if (!open) return
    async function init() {
      const supabase = createClient()
      const { data } = await supabase.auth.mfa.listFactors()
      const verified = data?.totp?.find((f) => f.status === 'verified')
      if (verified) {
        setMfaStatus('on')
        setFactorId(verified.id)
      } else {
        setMfaStatus('off')
      }
      setMfaStep('idle')
      setMfaMsg(null)

      // Cüzdan bakiyesini çek
      try {
        const wallet = await getWalletBalance()
        setBalance(wallet.balance)
        setCurrency(wallet.currency)
      } catch {
        setBalance(0)
      }
    }
    init()
  }, [open])

  function toggle(section: 'email' | 'password' | '2fa') {
    setOpenSection((prev) => (prev === section ? null : section))
  }

  async function handleEmailConfirm() {
    setEmailLoading(true)
    setEmailMsg(null)

    try {
      const result = await updateEmail(newEmail)
      if (result.error) {
        setEmailMsg({ type: 'error', text: result.error })
      } else {
        setEmailMsg({ type: 'success', text: result.message! })
      }
    } catch {
      setEmailMsg({ type: 'error', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' })
    } finally {
      setEmailLoading(false)
      setEmailConfirm(false)
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPasswordLoading(true)
    setPasswordMsg(null)

    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Yeni şifreler eşleşmiyor.' })
      setPasswordLoading(false)
      return
    }

    try {
      const result = await updatePassword(currentPassword, newPassword)
      if (result.error) {
        setPasswordMsg({ type: 'error', text: result.error })
      } else {
        setPasswordMsg({ type: 'success', text: result.message! })
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      }
    } catch {
      setPasswordMsg({ type: 'error', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' })
    } finally {
      setPasswordLoading(false)
    }
  }

  async function handleEnrollMfa() {
    setMfaLoading(true)
    setMfaMsg(null)
    const supabase = createClient()
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' })
    if (error) {
      setMfaMsg({ type: 'error', text: error.message })
      setMfaLoading(false)
      return
    }
    setQrCode(data.totp.qr_code)
    setSecret(data.totp.secret)
    setFactorId(data.id)
    setMfaStep('verifying')
    setMfaLoading(false)
  }

  async function handleVerifyMfa() {
    setMfaLoading(true)
    setMfaMsg(null)
    const supabase = createClient()
    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })
    if (challengeError) {
      setMfaMsg({ type: 'error', text: challengeError.message })
      setMfaLoading(false)
      return
    }
    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code: totpCode,
    })
    if (verifyError) {
      setMfaMsg({ type: 'error', text: 'Doğrulama kodu hatalı. Tekrar deneyin.' })
      setMfaLoading(false)
      return
    }
    setMfaStatus('on')
    setMfaStep('idle')
    setTotpCode('')
    setQrCode('')
    setSecret('')
    setMfaMsg({ type: 'success', text: '2FA başarıyla etkinleştirildi.' })
    setMfaLoading(false)
  }

  async function handleUnenrollMfa() {
    setMfaLoading(true)
    setMfaMsg(null)
    const supabase = createClient()
    const { error } = await supabase.auth.mfa.unenroll({ factorId })
    if (error) {
      setMfaMsg({ type: 'error', text: error.message })
      setMfaLoading(false)
      return
    }
    setMfaStatus('off')
    setMfaStep('idle')
    setFactorId('')
    setMfaMsg({ type: 'success', text: '2FA devre dışı bırakıldı.' })
    setMfaLoading(false)
  }

  return (
    <SlideOverSheet open={open} onClose={onClose} title="Hesabım" size="sm">
      {/* Cüzdan Bakiyesi */}
      <div className="rounded-lg border p-4 space-y-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2 text-primary">
            <WalletIcon className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Cüzdan Bakiyesi</p>
            <p className="text-lg font-semibold">
              {balance === null ? '—' : formatCurrency(balance, currency)}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full" asChild onClick={onClose}>
          <Link href="/admin/hesabim">
            Tüm Detaylar
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </div>

      <Separator className="mb-4" />

      <div className="space-y-2">
        {/* E-posta Accordion */}
        <div className="rounded-lg border">
          <button
            type="button"
            onClick={() => toggle('email')}
            className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors rounded-lg"
          >
            <Mail className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">E-posta Adresi</p>
              <p className="text-xs text-muted-foreground truncate">{email}</p>
            </div>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${openSection === 'email' ? 'rotate-180' : ''}`}
            />
          </button>

          {openSection === 'email' && (
            <div className="border-t px-4 py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Yeni E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setEmailConfirm(false); setEmailMsg(null) }}
                />
              </div>
              {emailMsg && (
                <p className={`text-sm ${emailMsg.type === 'error' ? 'text-destructive' : 'text-green-600'}`}>
                  {emailMsg.text}
                </p>
              )}
              {emailConfirm && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
                  <p className="text-sm text-amber-200">
                    E-posta <span className="font-medium">{newEmail}</span> olarak güncellenecek. Onaylıyor musunuz?
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" disabled={emailLoading} onClick={handleEmailConfirm}>
                      {emailLoading ? 'Güncelleniyor...' : 'Evet, Güncelle'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEmailConfirm(false)} disabled={emailLoading}>
                      İptal
                    </Button>
                  </div>
                </div>
              )}
              {!emailConfirm && (
                <Button
                  disabled={emailLoading || newEmail === email || !newEmail}
                  size="sm"
                  onClick={() => { setEmailConfirm(true); setEmailMsg(null) }}
                >
                  E-postayı Güncelle
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Şifre Accordion */}
        <div className="rounded-lg border">
          <button
            type="button"
            onClick={() => toggle('password')}
            className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors rounded-lg"
          >
            <Lock className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm font-medium">Şifre</p>
              <p className="text-xs text-muted-foreground">Şifrenizi değiştirin</p>
            </div>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${openSection === 'password' ? 'rotate-180' : ''}`}
            />
          </button>

          {openSection === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="border-t px-4 py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Mevcut Şifre</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">Yeni Şifre</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Yeni Şifre (Tekrar)</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              {passwordMsg && (
                <p className={`text-sm ${passwordMsg.type === 'error' ? 'text-destructive' : 'text-green-600'}`}>
                  {passwordMsg.text}
                </p>
              )}
              <Button type="submit" disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword} size="sm">
                {passwordLoading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
              </Button>
            </form>
          )}
        </div>

        {/* 2FA Accordion */}
        <div className="rounded-lg border">
          <button
            type="button"
            onClick={() => toggle('2fa')}
            className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors rounded-lg"
          >
            {mfaStatus === 'on' ? (
              <ShieldCheck className="h-4 w-4 text-green-600" />
            ) : (
              <Shield className="h-4 w-4 text-muted-foreground" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">İki Faktörlü Doğrulama</p>
              <p className="text-xs text-muted-foreground">
                {mfaStatus === 'loading' ? 'Yükleniyor...' : mfaStatus === 'on' ? 'Aktif' : 'Kapalı'}
              </p>
            </div>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${openSection === '2fa' ? 'rotate-180' : ''}`}
            />
          </button>

          {openSection === '2fa' && (
            <div className="border-t px-4 py-4 space-y-4">
              {mfaStatus === 'off' && mfaStep === 'idle' && (
                <>
                  <p className="text-sm text-muted-foreground">
                    Hesabınızı daha güvenli hale getirmek için iki faktörlü doğrulamayı etkinleştirin.
                  </p>
                  <Button onClick={handleEnrollMfa} disabled={mfaLoading} size="sm">
                    {mfaLoading ? 'Hazırlanıyor...' : 'İki Faktörlü Doğrulamayı Etkinleştir'}
                  </Button>
                </>
              )}

              {mfaStep === 'verifying' && (
                <>
                  <p className="text-sm text-muted-foreground">
                    Google Authenticator, Authy veya benzeri bir uygulama ile QR kodu okutun.
                  </p>
                  <div className="flex justify-center py-2">
                    <img src={qrCode} alt="2FA QR Kod" className="w-48 h-48" />
                  </div>
                  <div className="rounded-md bg-muted px-3 py-2 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Kurulum Anahtarı</p>
                    <p className="text-sm font-mono font-medium select-all break-all">{secret}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="totpCode">Doğrulama Kodu</Label>
                    <Input
                      id="totpCode"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="6 haneli kod"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleVerifyMfa} disabled={mfaLoading || totpCode.length !== 6} size="sm">
                      {mfaLoading ? 'Doğrulanıyor...' : 'Doğrula ve Etkinleştir'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setMfaStep('idle'); setQrCode(''); setSecret(''); setTotpCode(''); setMfaMsg(null) }}
                      size="sm"
                    >
                      İptal
                    </Button>
                  </div>
                </>
              )}

              {mfaStatus === 'on' && mfaStep === 'idle' && (
                <>
                  <p className="text-sm text-muted-foreground">
                    İki faktörlü doğrulama aktif. Hesabınız ek güvenlik katmanı ile korunuyor.
                  </p>
                  <Button variant="destructive" onClick={() => setMfaStep('confirming-disable')} size="sm">
                    Devre Dışı Bırak
                  </Button>
                </>
              )}

              {mfaStep === 'confirming-disable' && (
                <>
                  <p className="text-sm text-destructive font-medium">
                    İki faktörlü doğrulamayı devre dışı bırakmak istediğinizden emin misiniz?
                  </p>
                  <div className="flex gap-2">
                    <Button variant="destructive" onClick={handleUnenrollMfa} disabled={mfaLoading} size="sm">
                      {mfaLoading ? 'Kaldırılıyor...' : 'Evet, Devre Dışı Bırak'}
                    </Button>
                    <Button variant="outline" onClick={() => { setMfaStep('idle'); setMfaMsg(null) }} size="sm">
                      İptal
                    </Button>
                  </div>
                </>
              )}

              {mfaMsg && (
                <p className={`text-sm ${mfaMsg.type === 'error' ? 'text-destructive' : 'text-green-600'}`}>
                  {mfaMsg.text}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </SlideOverSheet>
  )
}
